import { useEffect, useRef } from "react";
import * as maplibregl from "maplibre-gl";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { getStationBrand } from "./brands.js";
import { comma } from "./format.js";
import { prefersReducedMotion } from "./ui.jsx";

maplibregl.setWorkerUrl(workerUrl);

const STYLES = { dark: "https://tiles.openfreemap.org/styles/dark", light: "https://tiles.openfreemap.org/styles/positron" };
const PALETTE = {
  dark: { surface: "#141c21", ink: "#e8eef0", muted: "#8fa0a9", line: "#26323a", teal: "#3fd1be", amber: "#f2c14e", camera: "#3d7be0", origin: "#5aa8ff", casing: "#0e1418", destination: "#b48cff" },
  light: { surface: "#ffffff", ink: "#14202a", muted: "#5e6e78", line: "#cfd8d8", teal: "#0b7a6e", amber: "#e0a91f", camera: "#2f6fd6", origin: "#2b7de9", casing: "#ffffff", destination: "#8a5cf0" },
};
const STATION_PIN_ZOOM = 10;
const CAMERA_PREVIEW_ZOOM = 13;
const CAMERA_REFRESH_MS = 120000;
const mapResource = (url) => `/api/map-resource?url=${encodeURIComponent(url)}`;
const EMPTY = { type: "FeatureCollection", features: [] };
const point = (id, lng, lat, properties = {}) => ({ type: "Feature", geometry: { type: "Point", coordinates: [lng, lat] }, properties: { id, ...properties } });
const line = (coordinates) => (coordinates?.length > 1 ? { type: "FeatureCollection", features: [{ type: "Feature", geometry: { type: "LineString", coordinates }, properties: {} }] } : EMPTY);
const places = (origin, destination, tripMode) => ({ type: "FeatureCollection", features: [
  origin && point("origin", origin.lng, origin.lat, { kind: "origin" }),
  tripMode === "enRoute" && destination && point("destination", destination.lng, destination.lat, { kind: "destination" }),
].filter(Boolean) });

function stationsData(stations, requestedStation, selectedId, bestId, priceMode = "trip") {
  const unique = new Map([...stations, requestedStation].filter(Boolean).map((station) => [station.id, station]));
  return { type: "FeatureCollection", features: [...unique.values()].map((station) => {
    const brand = getStationBrand(station);
    const trip = station.estimate?.effectivePrice ?? station.pumpPrice ?? 0;
    const pump = station.pumpPrice ?? trip;
    const shown = priceMode === "pump" ? pump : trip;
    return point(station.id, station.lng, station.lat, {
      name: station.name,
      trip,
      pump,
      shown,
      label: comma(shown, 3),
      brandColor: brand?.color || "#8a9aa3",
      requested: Number(station.id === requestedStation?.id),
      best: Number(station.id === bestId),
      selected: Number(station.id === selectedId && station.id !== bestId),
    });
  }) };
}

const camerasData = (cameras, selectedCamera) => ({ type: "FeatureCollection", features: cameras
  .filter((camera) => Number.isFinite(camera.lat) && Number.isFinite(camera.lng))
  .map((camera) => point(camera.id, camera.lng, camera.lat, { name: camera.name, selected: Number(camera.id === selectedCamera?.id) })) });

const clusterText = (field) => ["format",
  ["get", "point_count_abbreviated"], { "font-scale": 1 }, "\n", {},
  "desde ", { "font-scale": 0.72 },
  ["number-format", ["get", field], { locale: "es-ES", "min-fraction-digits": 3, "max-fraction-digits": 3 }], { "font-scale": 0.72 },
];

// Rótulos del mapa en español cuando el estilo los trae.
function localizeLabels(map) {
  for (const layer of map.getStyle()?.layers || []) {
    if (layer.type !== "symbol") continue;
    const field = map.getLayoutProperty(layer.id, "text-field");
    if (field && JSON.stringify(field).includes("name")) {
      map.setLayoutProperty(layer.id, "text-field", ["coalesce", ["get", "name:es"], ["get", "name"]]);
    }
  }
}

function addLayers(map, colors, priceMode = "trip") {
  map.addSource("stations", { type: "geojson", data: EMPTY, cluster: true, clusterRadius: 52, clusterMaxZoom: 11, clusterProperties: { minTrip: ["min", ["get", "trip"]], minPump: ["min", ["get", "pump"]] } });
  map.addLayer({ id: "station-clusters", type: "circle", source: "stations", filter: ["has", "point_count"], paint: { "circle-color": colors.surface, "circle-radius": ["step", ["get", "point_count"], 25, 20, 29, 60, 33], "circle-stroke-color": colors.line, "circle-stroke-width": 1.5 } });
  map.addLayer({ id: "station-count", type: "symbol", source: "stations", filter: ["has", "point_count"], layout: { "text-field": clusterText(priceMode === "pump" ? "minPump" : "minTrip"), "text-size": 14, "text-line-height": 1.05, "text-allow-overlap": true }, paint: { "text-color": colors.ink } });
  map.addLayer({ id: "station-points", type: "circle", source: "stations", filter: ["!", ["has", "point_count"]], paint: {
    "circle-color": ["case", ["==", ["get", "best"], 1], colors.teal, ["any", ["==", ["get", "selected"], 1], ["==", ["get", "requested"], 1]], colors.amber, colors.muted],
    "circle-radius": 4.5, "circle-stroke-color": colors.casing, "circle-stroke-width": 1.5,
  } });
  map.addSource("cameras", { type: "geojson", data: EMPTY, cluster: true, clusterRadius: 52, clusterMaxZoom: 12 });
  map.addLayer({ id: "camera-clusters", type: "circle", source: "cameras", filter: ["has", "point_count"], paint: { "circle-color": colors.camera, "circle-radius": ["step", ["get", "point_count"], 18, 20, 23, 60, 28], "circle-stroke-color": colors.casing, "circle-stroke-width": 2 } });
  map.addLayer({ id: "camera-count", type: "symbol", source: "cameras", filter: ["has", "point_count"], layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": 13, "text-allow-overlap": true }, paint: { "text-color": "#ffffff" } });
  map.addLayer({ id: "camera-points", type: "circle", source: "cameras", filter: ["!", ["has", "point_count"]], paint: { "circle-color": ["case", ["==", ["get", "selected"], 1], colors.amber, colors.camera], "circle-radius": ["case", ["==", ["get", "selected"], 1], 10, 7], "circle-stroke-color": "#ffffff", "circle-stroke-width": 2 } });
  map.addSource("route", { type: "geojson", data: EMPTY });
  map.addLayer({ id: "route-casing", type: "line", source: "route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": colors.casing, "line-width": 9, "line-opacity": 0.9 } }, "station-clusters");
  map.addLayer({ id: "route-line", type: "line", source: "route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": colors.teal, "line-width": 4.5 } }, "station-clusters");
  map.addLayer({ id: "route-estimate", type: "line", source: "route", layout: { visibility: "none", "line-cap": "round" }, paint: { "line-color": colors.muted, "line-width": 3, "line-dasharray": [1.5, 2] } }, "station-clusters");
  map.addSource("places", { type: "geojson", data: EMPTY });
  map.addLayer({ id: "place-points", type: "circle", source: "places", paint: { "circle-color": ["match", ["get", "kind"], "destination", colors.destination, colors.origin], "circle-radius": 8, "circle-stroke-color": "#ffffff", "circle-stroke-width": 3 } });
}

function setVisibility(map, ids, visible) {
  for (const id of ids) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
}

function applyMode(map, { mode, fuelMapLayer, routeEstimated, routeTone, theme }) {
  const fuel = mode === "fuel";
  setVisibility(map, ["station-clusters", "station-count", "station-points"], fuel && fuelMapLayer === "stations");
  setVisibility(map, ["camera-clusters", "camera-count", "camera-points"], mode === "cameras" || (fuel && fuelMapLayer === "cameras"));
  setVisibility(map, ["place-points"], fuel);
  setVisibility(map, ["route-casing", "route-line"], fuel && !routeEstimated);
  setVisibility(map, ["route-estimate"], fuel && routeEstimated);
  const colors = PALETTE[theme] || PALETTE.dark;
  if (map.getLayer("route-line")) map.setPaintProperty("route-line", "line-color", routeTone === "selected" ? colors.amber : colors.teal);
}

export default function MapView({ priceMode = "trip", theme, mode = "fuel", fuelMapLayer = "stations", origin, destination, tripMode, stations = [], requestedStation, selectedId, bestId, cameras = [], contextCameras = [], selectedCamera, routeGeometry, routeEstimated, routeTone, insets, fitTarget, onSelect, onCameraSelect, onAreaChange, onMapError }) {
  const fittedKey = useRef("");
  const container = useRef(null);
  const mapRef = useRef(null);
  const latest = useRef({});
  const previewMarkers = useRef(new Map());
  const stationMarkers = useRef(new Map());
  const loadedTheme = useRef(theme);
  latest.current = { priceMode, theme, mode, fuelMapLayer, origin, destination, tripMode, stations, requestedStation, selectedId, bestId, cameras, contextCameras, selectedCamera, routeGeometry, routeEstimated, routeTone, insets, fitTarget, onSelect, onCameraSelect, onAreaChange, onMapError };

  const motion = () => ({ duration: prefersReducedMotion() ? 0 : 500, padding: { top: 16, left: 16, right: 16, bottom: 16, ...latest.current.insets } });

  useEffect(() => {
    if (!container.current) return undefined;
    let map;
    try {
      map = new maplibregl.Map({
        container: container.current,
        style: mapResource(STYLES[theme] || STYLES.dark),
        transformRequest: (url) => ({ url: url.startsWith("https://tiles.openfreemap.org/") ? mapResource(url) : url }),
        center: [-3.7, 40.2], zoom: 5.3, minZoom: 4, maxZoom: 18, maxBounds: [[-19.5, 27], [5, 45]],
        attributionControl: { compact: true },
        fadeDuration: 150,
      });
    } catch (error) {
      latest.current.onMapError?.(error.message || "El navegador no pudo iniciar el mapa");
      return undefined;
    }
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");

    const clearStationMarkers = () => { for (const marker of stationMarkers.current.values()) marker.remove(); stationMarkers.current.clear(); };
    const clearPreviewMarkers = () => { for (const marker of previewMarkers.current.values()) marker.remove(); previewMarkers.current.clear(); };

    map.on("style.load", () => {
      const p = latest.current;
      localizeLabels(map);
      addLayers(map, PALETTE[loadedTheme.current] || PALETTE.dark, p.priceMode);
      map.getSource("stations").setData(stationsData(p.stations, p.requestedStation, p.selectedId, p.bestId, p.priceMode));
      map.getSource("cameras").setData(camerasData(p.mode === "cameras" ? p.cameras : p.contextCameras, p.selectedCamera));
      map.getSource("route").setData(line(p.routeGeometry));
      map.getSource("places").setData(places(p.origin, p.destination, p.tripMode));
      applyMode(map, { ...p, theme: loadedTheme.current });
      clearStationMarkers();
      clearPreviewMarkers();
    });

    let fallbackAttempted = false;
    map.on("error", (event) => {
      // Una tesela o una fuente que fallan no son motivo para cambiar de estilo.
      if (event.sourceId || event.tile) return;
      if (!fallbackAttempted && loadedTheme.current === "dark") {
        fallbackAttempted = true;
        loadedTheme.current = "light";
        map.setStyle(mapResource(STYLES.light));
        latest.current.onMapError?.("El mapa oscuro no ha cargado; se muestra el claro.");
      } else latest.current.onMapError?.("No se pudo cargar el mapa. Revisa la conexión y recarga la página.");
    });

    map.on("dragend", () => { const center = map.getCenter(); latest.current.onAreaChange?.({ lat: center.lat, lng: center.lng }); });
    for (const [layer, source] of [["station-clusters", "stations"], ["camera-clusters", "cameras"]]) {
      map.on("click", layer, async (event) => {
        const id = event.features?.[0]?.properties?.cluster_id;
        if (id == null) return;
        try {
          const zoom = await map.getSource(source).getClusterExpansionZoom(id);
          map.easeTo({ ...motion(), center: event.features[0].geometry.coordinates, zoom });
        } catch { /* La fuente cambió durante un cambio de modo. */ }
      });
    }
    map.on("click", "station-points", (event) => { const id = event.features?.[0]?.properties?.id; if (id) latest.current.onSelect?.(String(id)); });
    map.on("click", "camera-points", (event) => { const id = event.features?.[0]?.properties?.id; if (id) latest.current.onCameraSelect?.(String(id)); });

    // Etiquetas de precio: se actualizan en su sitio y nunca animan su posición.
    const updateStationMarkers = () => {
      const p = latest.current;
      const active = p.mode === "fuel" && p.fuelMapLayer === "stations" && map.getZoom() >= STATION_PIN_ZOOM && map.getLayer("station-points");
      const visible = active ? map.queryRenderedFeatures({ layers: ["station-points"] }) : [];
      const unique = new Map();
      for (const feature of visible) {
        const id = String(feature.properties?.id || "");
        if (id && !unique.has(id)) unique.set(id, feature);
      }
      const priority = (f) => Number(f.properties.selected) * 4 + Number(f.properties.requested) * 4 + Number(f.properties.best) * 2;
      const candidates = [...unique.values()].sort((a, b) => priority(b) - priority(a) || a.properties.shown - b.properties.shown);
      const occupied = [];
      const wanted = [];
      for (const feature of candidates) {
        const screen = map.project(feature.geometry.coordinates);
        if (!priority(feature) && occupied.some((spot) => Math.abs(spot.x - screen.x) < 74 && Math.abs(spot.y - screen.y) < 34)) continue;
        occupied.push(screen);
        wanted.push(feature);
        if (wanted.length >= 60) break;
      }
      const wantedIds = new Set(wanted.map((feature) => String(feature.properties.id)));
      for (const [id, marker] of stationMarkers.current) if (!wantedIds.has(id)) { marker.remove(); stationMarkers.current.delete(id); }
      for (const feature of wanted) {
        const { id, label, brandColor, selected, best, requested, name } = feature.properties;
        const key = String(id);
        let marker = stationMarkers.current.get(key);
        if (!marker) {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "map-pin";
          const swatch = document.createElement("i");
          const text = document.createElement("span");
          button.append(swatch, text);
          button.addEventListener("click", (event) => { event.stopPropagation(); latest.current.onSelect?.(key); });
          marker = new maplibregl.Marker({ element: button, anchor: "bottom", offset: [0, -6] }).setLngLat(feature.geometry.coordinates).addTo(map);
          stationMarkers.current.set(key, marker);
        }
        const element = marker.getElement();
        element.classList.toggle("is-best", Boolean(Number(best)));
        element.classList.toggle("is-pump", latest.current.priceMode === "pump");
        element.classList.toggle("is-selected", Boolean(Number(selected) || Number(requested)));
        element.style.zIndex = Number(selected) || Number(requested) ? "3" : Number(best) ? "2" : "1";
        element.firstChild.style.background = brandColor;
        element.lastChild.textContent = label;
        element.setAttribute("aria-label", `${name}, ${label} euros por litro ${latest.current.priceMode === "pump" ? "en surtidor" : "con desplazamiento"}`);
      }
    };
    map.on("idle", updateStationMarkers);
    map.on("moveend", updateStationMarkers);

    const updateCameraPreviews = () => {
      const p = latest.current;
      const active = (p.mode === "cameras" || (p.mode === "fuel" && p.fuelMapLayer === "cameras")) && map.getZoom() >= CAMERA_PREVIEW_ZOOM && map.getLayer("camera-points");
      const visible = active ? map.queryRenderedFeatures({ layers: ["camera-points"] }) : [];
      const unique = new Map();
      for (const feature of visible) {
        const id = String(feature.properties?.id || "");
        if (/^\d+$/.test(id) && !unique.has(id)) unique.set(id, feature);
      }
      const selected = p.selectedCamera?.id;
      const wanted = [...unique.values()].sort((left, right) => Number(right.properties.id === selected) - Number(left.properties.id === selected)).slice(0, 6);
      const wantedIds = new Set(wanted.map((feature) => String(feature.properties.id)));
      for (const [id, marker] of previewMarkers.current) if (!wantedIds.has(id)) { marker.remove(); previewMarkers.current.delete(id); }
      for (const feature of wanted) {
        const id = String(feature.properties.id);
        if (previewMarkers.current.has(id)) continue;
        const button = document.createElement("button");
        button.type = "button";
        button.className = "camera-preview";
        button.setAttribute("aria-label", `Abrir cámara ${feature.properties.name}`);
        const image = document.createElement("img");
        image.src = `/api/camera-image?id=${id}`; image.alt = ""; image.loading = "lazy";
        image.addEventListener("error", () => button.classList.add("is-broken"));
        const label = document.createElement("span"); label.textContent = feature.properties.name;
        button.append(image, label);
        button.addEventListener("click", (event) => { event.stopPropagation(); latest.current.onCameraSelect?.(id); });
        previewMarkers.current.set(id, new maplibregl.Marker({ element: button, anchor: "bottom", offset: [0, -12] }).setLngLat(feature.geometry.coordinates).addTo(map));
      }
    };
    map.on("idle", updateCameraPreviews);
    map.on("moveend", updateCameraPreviews);
    const previewRefresh = window.setInterval(() => {
      for (const [id, marker] of previewMarkers.current) marker.getElement().querySelector("img").src = `/api/camera-image?id=${id}&t=${Date.now()}`;
    }, CAMERA_REFRESH_MS);

    for (const layer of ["station-clusters", "station-points", "camera-clusters", "camera-points"]) {
      map.on("mouseenter", layer, () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", layer, () => { map.getCanvas().style.cursor = ""; });
    }
    map.clearStationMarkers = clearStationMarkers;
    map.clearPreviewMarkers = clearPreviewMarkers;
    return () => { window.clearInterval(previewRefresh); clearPreviewMarkers(); clearStationMarkers(); map.remove(); mapRef.current = null; };
    // El mapa se crea una sola vez; los cambios llegan por los efectos de abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !STYLES[theme] || theme === loadedTheme.current) return;
    loadedTheme.current = theme;
    map.setStyle(mapResource(STYLES[theme]));
  }, [theme]);
  useEffect(() => { mapRef.current?.getSource("stations")?.setData(stationsData(stations, requestedStation, selectedId, bestId, priceMode)); }, [stations, requestedStation, selectedId, bestId, priceMode]);
  useEffect(() => {
    const map = mapRef.current;
    if (map?.getLayer("station-count")) map.setLayoutProperty("station-count", "text-field", clusterText(priceMode === "pump" ? "minPump" : "minTrip"));
  }, [priceMode]);
  useEffect(() => { mapRef.current?.getSource("cameras")?.setData(camerasData(mode === "cameras" ? cameras : contextCameras, selectedCamera)); }, [cameras, contextCameras, selectedCamera, mode]);
  useEffect(() => { mapRef.current?.getSource("route")?.setData(line(routeGeometry)); }, [routeGeometry]);
  useEffect(() => { mapRef.current?.getSource("places")?.setData(places(origin, destination, tripMode)); }, [destination, origin, tripMode]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    applyMode(map, { mode, fuelMapLayer, routeEstimated, routeTone, theme: loadedTheme.current });
  }, [mode, fuelMapLayer, routeEstimated, routeTone]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (mode !== "fuel" || fuelMapLayer !== "stations") map.clearStationMarkers?.();
    if (mode === "fuel" && fuelMapLayer !== "cameras") map.clearPreviewMarkers?.();
  }, [mode, fuelMapLayer]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode !== "fuel" || !origin) return;
    if (tripMode === "enRoute" && destination) {
      map.fitBounds([[Math.min(origin.lng, destination.lng), Math.min(origin.lat, destination.lat)], [Math.max(origin.lng, destination.lng), Math.max(origin.lat, destination.lat)]], { ...motion(), maxZoom: 12, padding: { top: 60, left: 60, right: 60, bottom: 60 + (latest.current.insets?.bottom || 0) } });
    } else if (!(latest.current.fitTarget?.points?.length >= 2)) map.easeTo({ ...motion(), center: [origin.lng, origin.lat], zoom: 12.5 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, origin, tripMode]);
  // Tras una búsqueda, encuadra el origen y las mejores opciones en la zona visible del mapa.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode !== "fuel" || !fitTarget || fitTarget.points.length < 2 || fittedKey.current === fitTarget.key) return;
    fittedKey.current = fitTarget.key;
    const lngs = fitTarget.points.map((item) => item.lng);
    const lats = fitTarget.points.map((item) => item.lat);
    const extra = latest.current.insets || {};
    map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], {
      duration: prefersReducedMotion() ? 0 : 500,
      maxZoom: 14,
      padding: { top: 56 + (extra.top || 0), bottom: 24 + (extra.bottom || 0), left: 48, right: 48 },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitTarget?.key, fitTarget?.points.length, mode]);
  useEffect(() => {
    const map = mapRef.current;
    const station = stations.find((item) => item.id === selectedId);
    if (!map || mode !== "fuel" || !station) return;
    const screen = map.project([station.lng, station.lat]);
    const { clientWidth, clientHeight } = map.getContainer();
    const bottom = clientHeight - (latest.current.insets?.bottom || 0);
    if (screen.x < 40 || screen.y < 60 || screen.x > clientWidth - 40 || screen.y > bottom - 20) map.easeTo({ ...motion(), center: [station.lng, station.lat] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);
  useEffect(() => {
    if (mode === "fuel" && requestedStation) mapRef.current?.easeTo({ ...motion(), center: [requestedStation.lng, requestedStation.lat], zoom: Math.max(mapRef.current.getZoom(), 13.5) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, requestedStation?.id]);
  useEffect(() => {
    if ((mode === "cameras" || fuelMapLayer === "cameras") && selectedCamera) mapRef.current?.easeTo({ ...motion(), center: [selectedCamera.lng, selectedCamera.lat], zoom: Math.max(mapRef.current.getZoom(), 13.5) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, fuelMapLayer, selectedCamera?.id]);

  return <div ref={container} className="map" aria-label={mode === "fuel" ? "Mapa de gasolineras" : "Mapa de cámaras de la DGT"} />;
}
